import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { HOME_QUICK_GROUP_FILTERS, HOME_QUICK_PERSON_LABELS } from "../lib/home-quick-search";
import { getVisitorSubmitterKey } from "../lib/visitor-submitter";

const JAPAN_PREFECTURES = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県", "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県", "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県", "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県",
];

const FAN_GROUP_BUTTONS = [...HOME_QUICK_GROUP_FILTERS.map((g) => g.label), "その他"] as const;
const FAN_ARTIST_BUTTONS = [...HOME_QUICK_PERSON_LABELS.filter((name) => name !== "宮本佳林"), "その他"] as const;

function isAllowedSourceUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" && (url.hostname === "helloproject.com" || url.hostname === "www.jp-r.co.jp");
  } catch {
    return false;
  }
}

export function SubmitEventForm({ initialDate = "", showBackLink = false }: { initialDate?: string; showBackLink?: boolean }) {
  const emptyDraft = {
    fan_group_name: "",
    fan_artist_name: "",
    event_date: initialDate,
    prefecture: "",
    performance_count: "1",
    detail_url: "",
    source_title: "",
    message: "",
  };
  const [draft, setDraft] = useState({
    ...emptyDraft,
  });
  const [busy, setBusy] = useState(false);
  const [titleMsg, setTitleMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!initialDate) return;
    setDraft((d) => ({ ...d, event_date: initialDate }));
  }, [initialDate]);

  const fetchTitle = async () => {
    setTitleMsg(null);
    setErr(null);
    if (!isAllowedSourceUrl(draft.detail_url)) {
      setTitleMsg("URLは helloproject.com または www.jp-r.co.jp の https ページだけ登録できます。");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/public/source-title", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url: draft.detail_url.trim() }),
      });
      const j = (await res.json().catch(() => ({}))) as { title?: string | null; error?: string };
      if (!res.ok) throw new Error(j.error ?? `HTTP ${res.status}`);
      if (j.title) {
        setDraft((d) => ({ ...d, source_title: j.title ?? "" }));
        setTitleMsg("タイトルを取得しました。タイトルはURLから取得したものだけ使います。");
      } else {
        setTitleMsg("タイトルを取得できませんでした。このURLでは投稿できません。");
      }
    } catch {
      setTitleMsg("タイトルを取得できませんでした。このURLでは投稿できません。");
    } finally {
      setBusy(false);
    }
  };

  const clearDraft = () => {
    setDraft({ ...emptyDraft });
    setTitleMsg(null);
    setErr(null);
    setDone(false);
  };

  const submit = async () => {
    setErr(null);
    setDone(false);
    if ((!draft.fan_group_name.trim() && !draft.fan_artist_name.trim()) || !draft.event_date || !draft.prefecture || !draft.source_title.trim()) {
      setErr("未入力箇所があります");
      return;
    }
    if (!isAllowedSourceUrl(draft.detail_url)) {
      setErr("情報元URLは https://helloproject.com または https://www.jp-r.co.jp 配下だけ登録できます。");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/public/event-requests", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          kind: "add",
          message: draft.message.trim() || "（本文なし）",
          submitterKey: getVisitorSubmitterKey(),
          proposed: { ...draft, performance_count: Number(draft.performance_count), event_name: draft.source_title.trim() },
        }),
      });
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        if (j.error === "duplicate_event") throw new Error("この予定はすでに登録されています。重複投稿は反映しません。");
        if (j.error === "title_fetch_failed") throw new Error("URLからタイトルを取得できませんでした。このURLでは投稿できません。");
        if (j.error === "rate_limited") throw new Error("短時間の投稿回数が上限に達しました。時間をおいてからもう一度お願いします。");
        throw new Error(j.error ?? `HTTP ${res.status}`);
      }
      setDone(true);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
      <section className="card home-submit-page">
        {showBackLink ? <Link className="home-inline-link" to="/">← カレンダーへ戻る</Link> : null}
        <h1>こんなイベントもあるよ！</h1>
        <p>公式URLがある他グループ・他出演者の予定を投稿できます。投稿後はすぐ公開されます。</p>
        <p className="home-submit-delay-note">カレンダーの反映には時間がかかることがあります。</p>
        <p className="home-submit-limit-note">入力回数制限: 1時間に3件まで / 1日に10件まで</p>
        {done ? <p className="home-visitor-modal-err" style={{ color: "#2e7d32" }}>投稿しました。反映に少し時間がかかることがあります。</p> : null}
        {err ? <p className="home-visitor-modal-err">{err}</p> : null}
        <fieldset className="home-visitor-picker">
          <legend className="home-visitor-picker-legend">グループ名</legend>
          <p className="home-visitor-picker-hint">該当するグループを押してください。</p>
          <div className="row home-chips home-visitor-picker-chips">
            {FAN_GROUP_BUTTONS.map((name) => {
              const active = draft.fan_group_name === name;
              return (
                <button
                  key={name}
                  type="button"
                  className={`chip${active ? " active" : ""}`}
                  aria-pressed={active}
                  onClick={() => setDraft((d) => ({ ...d, fan_group_name: active ? "" : name }))}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </fieldset>
        <fieldset className="home-visitor-picker">
          <legend className="home-visitor-picker-legend">アーティスト名</legend>
          <p className="home-visitor-picker-hint">個人名で投稿する場合は押してください。</p>
          <div className="row home-chips home-visitor-picker-chips">
            {FAN_ARTIST_BUTTONS.map((name) => {
              const active = draft.fan_artist_name === name;
              return (
                <button
                  key={name}
                  type="button"
                  className={`chip${active ? " active" : ""}`}
                  aria-pressed={active}
                  onClick={() => setDraft((d) => ({ ...d, fan_artist_name: active ? "" : name }))}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </fieldset>
        <label className="home-visitor-field"><span>開催日</span><input className="input" type="date" value={draft.event_date} onChange={(e) => setDraft((d) => ({ ...d, event_date: e.target.value }))} /></label>
        <label className="home-visitor-field"><span>開催都道府県</span><select className="input" value={draft.prefecture} onChange={(e) => setDraft((d) => ({ ...d, prefecture: e.target.value }))}><option value="">選択してください</option>{JAPAN_PREFECTURES.map((p) => <option key={p} value={p}>{p}</option>)}</select></label>
        <label className="home-visitor-field"><span>公演数</span><input className="input" type="number" min={1} max={99} value={draft.performance_count} onChange={(e) => setDraft((d) => ({ ...d, performance_count: e.target.value }))} /></label>
        <label className="home-visitor-field"><span>情報元URL</span><input className="input" value={draft.detail_url} onChange={(e) => setDraft((d) => ({ ...d, detail_url: e.target.value, source_title: "" }))} placeholder="https://helloproject.com/... または https://www.jp-r.co.jp/..." /></label>
        <button type="button" className="btn secondary" disabled={busy || !draft.detail_url.trim()} onClick={() => void fetchTitle()}>{busy ? "取得中…" : "URLからタイトル取得"}</button>
        {titleMsg ? <p className="home-visitor-modal-lead">{titleMsg}</p> : null}
        <label className="home-visitor-field"><span>タイトル（URLから自動取得のみ）</span><input className="input" value={draft.source_title} readOnly placeholder="URLからタイトル取得を押してください" /></label>
        <label className="home-visitor-field"><span>補足・出典メモ（任意・管理者だけが確認）</span><textarea className="input home-visitor-textarea" rows={3} value={draft.message} onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value }))} /></label>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="btn" disabled={busy} onClick={() => void submit()}>{busy ? "投稿中…" : "投稿して即公開"}</button>
          <button type="button" className="btn secondary" disabled={busy} onClick={clearDraft}>内容クリア</button>
        </div>
      </section>
  );
}

export function SubmitEventPage() {
  const [params] = useSearchParams();
  const initialDate = useMemo(() => {
    const d = params.get("date") ?? "";
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : "";
  }, [params]);

  return (
    <div className="shell home-shell">
      <SubmitEventForm initialDate={initialDate} showBackLink />
    </div>
  );
}
